import React from 'react';
import { useTranslation } from 'react-i18next';

const CategoryScoresTable = ({ assessment, categoryScores }) => {
  const { t } = useTranslation();
  
  // Define maturity level colors to match the requested color scheme
  const maturityLevelColors = {
    'None': '#243444',       // Dark Blue (0%)
    'Initial': '#7C1227',    // Red (1-19%)
    'Defined': '#D2BA7B',    // Gold (20-49%)
    'Managed': '#4C5454',    // Gray (50-74%)
    'Compliant': '#3a7ca5',  // Blue (75-90%)
    'Optimized': '#27AE60'   // Green (91-100%)
  };

  // Function to get color based on score
  const getColorForScore = (score) => {
    if (score < 1) return maturityLevelColors['None'];
    if (score < 20) return maturityLevelColors['Initial'];
    if (score < 50) return maturityLevelColors['Defined'];
    if (score < 75) return maturityLevelColors['Managed'];
    if (score < 91) return maturityLevelColors['Compliant'];
    return maturityLevelColors['Optimized'];
  };

  // Function to get maturity level based on score
  const getMaturityLevelForScore = (score) => {
    if (score < 1) return 'None';
    if (score < 20) return 'Initial';
    if (score < 50) return 'Defined';
    if (score < 75) return 'Managed';
    if (score < 91) return 'Compliant';
    return 'Optimized';
  };

  // Filter out categories with 0% scores and sort by score (highest to lowest)
  const sortedCategories = Object.entries(categoryScores || {})
    .filter(([, score]) => score > 0) // Only include categories with scores > 0
    .sort(([, scoreA], [, scoreB]) => scoreB - scoreA);

  return (
    <div className="category-scores-table">
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#243444', color: '#FFFAFA' }}>
            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #D2BA7B' }}>
              {t('reports.category')}
            </th>
            <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #D2BA7B' }}>
              {t('reports.score')}
            </th>
            <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #D2BA7B' }}>
              {t('reports.maturityLevel')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedCategories.map(([category, score]) => {
            const maturityLevel = getMaturityLevelForScore(score);
            const color = getColorForScore(score);
            
            return (
              <tr key={category} style={{ borderBottom: '1px solid #e0e0e0' }}>
                <td style={{ padding: '10px', fontWeight: 'bold' }}>
                  {category}
                </td>
                <td style={{ padding: '10px', textAlign: 'center' }}>
                  <div style={{ 
                    display: 'inline-block',
                    width: '40px', 
                    height: '40px', 
                    borderRadius: '50%', 
                    backgroundColor: color,
                    color: '#FFFAFA',
                    lineHeight: '40px',
                    fontWeight: 'bold'
                  }}>
                    {Math.round(score)}%
                  </div>
                </td>
                <td style={{ padding: '10px', textAlign: 'center', color }}>
                  {maturityLevel}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      
      {/* Add overall score row */}
      {assessment && assessment.score && (
        <div style={{ 
          marginTop: '20px', 
          padding: '15px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h4 style={{ margin: 0, color: '#243444' }}>{t('reports.overallMaturity')}</h4>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              width: '60px', 
              height: '60px', 
              borderRadius: '50%', 
              backgroundColor: getColorForScore(assessment.score),
              color: '#FFFAFA',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '20px',
              marginRight: '15px'
            }}>
              {Math.round(assessment.score)}%
            </div>
            <div style={{ 
              fontWeight: 'bold', 
              fontSize: '18px',
              color: getColorForScore(assessment.score)
            }}>
              {getMaturityLevelForScore(assessment.score)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryScoresTable;
